import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'components/Atoms/Icon/Icon';
import { ResidentCustomPublicationConsent } from 'types/publishedAssets';
import styles from './NotificationSignup.module.scss';

interface Props {
  consents: ResidentCustomPublicationConsent[];
}

/**
 * Info trigger listing the consent copy for each list a resident is signing up
 * to. This is consent information, so it must be reachable without a pointer:
 * the trigger is a real button that opens on focus and click as well as hover,
 * and the panel is always in the DOM and referenced by `aria-describedby` so
 * assistive tech reads it regardless of visual state.
 */
export function ConsentInfoTooltip({ consents }: Props) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const panelId = `consent-info-${useId()}`;

  const describedConsents = consents.filter((consent) => consent.description?.trim());
  if (describedConsents.length === 0) {
    return null;
  }

  return (
    <span className={styles.consentInfo}>
      <button
        type="button"
        className={styles.consentInfoTrigger}
        aria-label={t('notificationSignup.consentInfoAria')}
        aria-describedby={panelId}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <Icon name="info" width={14} height={14} ariaHidden />
      </button>
      <span id={panelId} role="tooltip" className={isOpen ? styles.consentInfoPanel : styles.consentInfoPanelHidden}>
        <ul className={styles.consentInfoList}>
          {describedConsents.map((consent) => (
            <li key={consent.id}>
              {consent.title && <span className={styles.consentInfoTitle}>{consent.title}</span>}
              <span>{consent.description}</span>
            </li>
          ))}
        </ul>
      </span>
    </span>
  );
}
